import { Link } from "react-router-dom";
import { Compass, Crown, QrCode, RocketLaunch, Storefront } from "@phosphor-icons/react";
import { Shell } from "./Legal";

function Section({ title, children }) {
  return (
    <section>
      {title && <h2 className="font-display text-xl font-black text-white">{title}</h2>}
      <div className="mt-4 space-y-4 text-sm leading-relaxed text-zinc-300 md:text-base">{children}</div>
    </section>
  );
}

export default function About() {
  return (
    <Shell title="About NitePass" overline="Your Pass to the Night" legal={false}>
      <Section>
        <p className="text-base text-zinc-200 md:text-lg">
          NitePass was created with one vision: make going out <span className="font-bold text-neon-gold">easier, more connected, more rewarding, and more powerful</span> for everyone involved in nightlife.
        </p>
        <p>
          Finding somewhere to go shouldn't mean searching through dozens of social media pages, outdated flyers, group chats, and websites just to figure out what's happening tonight. And for venues and promoters, building a crowd shouldn't mean having no reliable way to know who actually brought people through the door.
        </p>
        <p className="font-bold text-white">NitePass brings the entire nightlife experience together.</p>
        <p>
          Discover upcoming events, clubs, bars, and lounges. See what's open, what it costs to get in, who's planning to attend, and what rewards are waiting for you. Then simply pull up and scan your NitePass.
        </p>
        <p>
          Your personal NitePass QR connects your nightlife experience to participating venues and events — making it possible to access guest lists, speed up entry, verify attendance, earn Nite Points, and unlock perks such as free entry, complimentary drinks, VIP experiences, exclusive offers, and more.
        </p>
      </Section>

      <Section title="More Than an Event App">
        <p>NitePass isn't being built to simply show you where the party is. <span className="font-bold text-white">We're building the technology behind the night.</span></p>
        <div className="mt-2 grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <Compass size={22} weight="duotone" className="text-neon-gold" />
            <div className="mt-3 font-display text-sm font-bold text-white">For Guests</div>
            <p className="mt-1 text-xs leading-relaxed text-zinc-400">A personalized nightlife passport — discover, scan in, get rewarded for being outside.</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <RocketLaunch size={22} weight="duotone" className="text-neon-violet" />
            <div className="mt-3 font-display text-sm font-bold text-white">For Promoters</div>
            <p className="mt-1 text-xs leading-relaxed text-zinc-400">Create events, build digital guest lists, send passes directly to guests, grow a following — with verified data showing who you brought through the door.</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <Storefront size={22} weight="duotone" className="text-neon-green" />
            <div className="mt-3 font-display text-sm font-bold text-white">For Venues</div>
            <p className="mt-1 text-xs leading-relaxed text-zinc-400">Manage events, entry points, promoters, guest traffic, loyalty rewards, promotions, and performance from one ecosystem.</p>
          </div>
        </div>
        <p>Every verified scan creates a connection between the guest, promoter, event, and venue.</p>
        <p className="font-bold text-white">Guests get rewarded. Promoters get credit. Venues get real data. <span className="text-neon-gold">Everybody wins.</span></p>
      </Section>

      <Section title="Meet the Founder & CEO">
        <div className="rounded-3xl border border-neon-gold/25 bg-[#0d0a03] p-6 md:p-8">
          <div className="flex items-center gap-5">
            <img src="/founder.jpg" alt="Jove Divinity — Founder & CEO of NitePass"
              className="h-32 w-28 shrink-0 rounded-2xl border-2 border-neon-gold/60 object-cover object-[50%_15%] shadow-glow-gold"
              data-testid="founder-photo" />
            <div>
              <div className="font-display text-xl font-black text-white" data-testid="founder-name">Jove Divinity</div>
              <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-neon-gold">Founder & CEO</div>
              <p className="mt-2 text-xs italic leading-relaxed text-zinc-400">"How can technology make the experience better?"</p>
            </div>
          </div>
          <div className="mt-5 space-y-4 text-sm leading-relaxed text-zinc-300">
            <p>
              NitePass was founded by Jove Divinity, an entrepreneur, creative visionary, and brand builder whose work lives at the intersection of technology, entertainment, culture, and community.
            </p>
            <p>
              Jove's approach to building brands starts with a simple question: <span className="font-bold text-white">How can technology make the experience better?</span>
            </p>
            <p>
              NitePass grew from recognizing a disconnect within nightlife. Guests want to know where everyone is going. Promoters want to know who actually showed up because of them. Venues want to know who is driving traffic through their doors. Yet much of nightlife still relies on disconnected flyers, social posts, screenshots, paper guest lists, word of mouth, and difficult-to-measure promotion.
            </p>
            <p>Jove saw an opportunity to connect those pieces. The result is NitePass.</p>
            <p>
              Rather than trying to replace nightlife culture, his vision is to build technology that amplifies it — giving guests better experiences while providing promoters and venues with tools that help them grow. His larger vision extends beyond discovering tonight's events: a platform capable of becoming a digital bridge between people, nightlife, entertainment, hospitality, and real-world experiences.
            </p>
          </div>
        </div>
      </Section>

      <Section title="Our Mission">
        <p className="text-base font-bold text-white md:text-lg">
          Connect the people who create the night with the people who live it.
        </p>
        <p>
          We're building a world where discovering somewhere to go takes seconds, your guest-list access lives directly on your phone, your favorite venues can reward your loyalty, promoters receive credit for the crowds they create, and businesses can understand their nightlife traffic in real time.
        </p>
        <p>
          From your first search… to the guest list… to the front door… to the reward you unlock once you're inside… NitePass is designed to travel through the entire night with you.
        </p>
      </Section>

      <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] py-12 text-center">
        <QrCode size={36} weight="duotone" className="mx-auto text-neon-gold" />
        <div className="mt-4 font-display text-lg font-black tracking-[0.2em] text-white">
          DISCOVER. CONNECT. SCAN. EXPERIENCE. <span className="text-neon-gold">GET REWARDED.</span>
        </div>
        <img src="/logo.png" alt="NITEPASS" className="mx-auto mt-6 h-16 w-auto" />
        <div className="mt-2 text-xs font-bold uppercase tracking-[0.3em] text-zinc-500">Your Pass to the Night</div>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <Link to="/auth?mode=register" className="btn-neon !px-6 !py-2.5 text-sm" data-testid="about-cta">Get the Pass — Free</Link>
          <Link to="/" className="btn-ghost-neon !px-6 !py-2.5 text-sm">Back to Home</Link>
        </div>
      </div>
    </Shell>
  );
}
